import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    Skeleton,
    Box,
    Button
  } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";

/**
 * table style
 */
  const StyledTable = styled(Table)(() => ({
    width: 800,
    borderCollapse: "separate",
    borderSpacing: 0,
    overflowX: 'hidden'
  }));

  const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",  
    height: "50px",
    fontSize: "10px",
    textAlign: "center", 
    borderBottom: "2px solid #ddd",
    borderTop: "2px solid #ddd",
    position: "sticky",
    top: 0,
    zIndex: 1,
    // 오른쪽 세로 구분선
    "&::after": {
      content: '""',
      position: "absolute",
      top: "25%",     // 세로선 길이 조절
      bottom: "25%",  // 세로선 길이 조절
      right: 0,
      width: "1.5px",
      backgroundColor: "#ddd",
    },
    "&:last-of-type::after": {
      content: "none", // 마지막 셀엔 선 없음
    },

  }));

  const StyledRow = styled(TableRow)(({ theme }) => ({
    "&:hover": {
      backgroundColor: "#f0f0f0",
    }
  }));

  const StyledCell = styled(TableCell)(() => ({
    borderBottom: "1px solid #e0e0e0",
    fontSize: "9px",
  }));


/**
 * 마우 오버 이미지 툴팁 시 레이지 로딩 처리
 * @param {*} param0 
 * @returns 
 */  
function LazyImageTooltip({ imageUrl, children }) {
  const [loaded, setLoaded] = useState(false);        // 이미지 로딩 완료 여부
  const [visible, setVisible] = useState(false);      // 컴포넌트가 뷰포트에 들어왔는지
  const containerRef = useRef(null);                  // 관찰 대상 ref

  // 컴포넌트가 화면에 들어오면 visible = true
  useEffect(() => {
    const node = containerRef.current; // 👈 ref를 로컬 변수로 저장
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.1 }
    );
  
    if (node) {
      observer.observe(node);
    }
  
    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, []);

  return (
    <div ref={containerRef}>
      <Tooltip
        title={
          <Box
            sx={{
              width: 150,
              height: 150,
              borderRadius: 2,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!loaded && <Skeleton variant="rectangular" width={150} height={150} />}
            {visible && (
              <img
                src={imageUrl}
                alt="미리보기"
                style={{
                  display: loaded ? "block" : "none",
                  width: 150,
                  height: "auto",
                  borderRadius: 8,
                }}
                onLoad={() => setLoaded(true)}
              />
            )}
          </Box>
        }
        arrow
        placement="right"
      >
        {children}
      </Tooltip>
    </div>
  );
}

export default function ProductTable({products, selectedProducts, onToggle, checkoutToggleAll, isLoading, CustomRankChangeCell}) {
  const skeletonRows = Array.from({ length: 13 });
  return (
    <StyledTable stickyHeader size="small">
      <TableHead sx={{ backgroundColor: "#fafafa" }}>
        <TableRow>
          <StyledHeaderCell sx={{width: 30, padding: 0}}>번<br/>호</StyledHeaderCell>
          <StyledHeaderCell sx={{width: 55, padding: 0}}>커머스</StyledHeaderCell>
          <StyledHeaderCell sx={{width: 65, padding: 0}}>상품<br/>이미지</StyledHeaderCell>
          <StyledHeaderCell sx={{width: 85, padding: 0}}>카테고리</StyledHeaderCell>
          {/* <StyledHeaderCell sx={{width: 110}}>카테고리<br/>(구분)</StyledHeaderCell>
          <StyledHeaderCell sx={{width: 110}}>카테고리<br/>(메인)</StyledHeaderCell> */}
          {/* <StyledHeaderCell sx={{width: 110}}>브랜드</StyledHeaderCell> */}
          <StyledHeaderCell>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              {/* 왼쪽: 버튼 */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<span>📈</span>}
                disabled={selectedProducts.length === 0}
                onClick={() => checkoutToggleAll([])}
                sx={{
                  backgroundColor: selectedProducts.length > 0 ? 'primary.main' : 'grey.300',
                  color: selectedProducts.length > 0 ? '#fff' : 'text.disabled',
                  '&:hover': {
                    backgroundColor: selectedProducts.length > 0 ? 'primary.dark' : 'grey.400',
                  },
                  fontSize: '9px',
                  px: 1.5,
                  minWidth: 'auto',
                  borderRadius: 1.5,
                  boxShadow: selectedProducts.length > 0 ? '0 2px 6px rgba(0,0,0,0.2)' : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                순위추이
              </Button>

              {/* 가운데: 상품명 텍스트 (자동 정렬 맞추기 위해 Box로 감쌈) */}
              <Box
                sx={{
                  flexGrow: 1,
                  textAlign: "center",
                  position: "absolute",
                  left: 0,
                  right: 0,
                  pointerEvents: "none" // 텍스트는 클릭 안됨
                }}
              >
                <Typography variant="body2" fontWeight="bold" fontSize={"10px"}>
                  [브랜드] 상품명
                </Typography>
              </Box>
            </Box>
          </StyledHeaderCell>
          <StyledHeaderCell sx={{width: 100}}>가격</StyledHeaderCell>
          <StyledHeaderCell sx={{width: 20}}>순<br/>위</StyledHeaderCell>
          <StyledHeaderCell sx={{width: 70}}>순위<br/>변동</StyledHeaderCell>
          <StyledHeaderCell sx={{width: 30}}>순위<br/>(avg7)</StyledHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
      {
      isLoading ? skeletonRows.map((_, i) => (
          <StyledRow key={i}>
            {Array.from({ length: 9 }).map((_, j) => (
              <StyledCell key={j}>
                <Skeleton variant="text" width="100%" height={44} sx={{
                  textAlign: "center", padding: 0
                }}/>
              </StyledCell>
            ))}
          </StyledRow>
        ))
      : products.length === 0 ? (
        <StyledRow>
          <StyledCell colSpan={9} align="center" height="680px">
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              opacity={0.5}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  backgroundColor: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 40
                }}
              >
                😔
              </Box>
              <Typography variant="body2" color="text.secondary" mt={1}>
                아쉽지만, 조건에 맞는 결과가 없습니다.
              </Typography>
            </Box>
          </StyledCell>
        </StyledRow>
      ) : (
        products.map((product, index) => (
          <StyledRow
            key={product.name}
            hover
            sx={{
              opacity:
                selectedProducts.length === 0 || selectedProducts.some(p => p.ProductName === product.ProductName)
                  ? 1
                  : 0.3,
                backgroundColor: selectedProducts.some(p => p.ProductName === product.ProductName)
                  ? '#e3f2fd' // 연한 파란색 (MUI theme의 info.light 계열)
                  : 'inherit',
                transition: "opacity 0.3s, background-color 0.3s",
            }}
          >
            <StyledCell sx={{textAlign: "center", padding: 0}}>
              {index+1}
            </StyledCell>
            <StyledCell sx={{textAlign: "center", padding: 0}}>{product.Commerce}</StyledCell>
            <StyledCell sx={{textAlign: "center", padding: "0.5px"}}>
              <LazyImageTooltip imageUrl={product.ProductImage}>
                <Typography
                  sx={{ cursor: "pointer", display: "block" }}
                  onClick={() => window.open(product.PageUrl, "_blank")}
                >
                  <img
                    src={product.ProductImage}
                    alt="product"
                    loading="lazy"
                    style={{ 
                      width: 55, 
                      height: 55, 
                      borderRadius: 6,
                      border: "1px solid #ccc",
                      boxSizing: "border-box",
                      display: "block",
                      margin: "0 auto"
                    }}
                  />
                </Typography>
              </LazyImageTooltip>
            </StyledCell>
            <StyledCell sx={{textAlign: "center", padding: 0}}>{product.CType}<br/>{product.CMain}</StyledCell>
            {/* <StyledCell sx={{textAlign: "center"}}>{product.CMain}</StyledCell> */}
            {/* <StyledCell sx={{textAlign: "center"}}>{product.Brand}</StyledCell> */}
            <StyledCell sx={{padding: 0}}>
              <Tooltip title="클릭 시 추이 그래프 확인" placement="bottom-start" arrow>
                <Typography
                  sx={{ 
                    fontSize: "9px", 
                    color: "primary.main", 
                    cursor: "pointer" 
                  }}
                  onClick={() => onToggle(product)}
                >
                  [{product.Brand}]<br/>{product.ProductName}
                </Typography>
              </Tooltip>
            </StyledCell>
            <StyledCell sx={{textAlign: "center", padding: 0}}>
              💰 정상가 : {product.SalePrice}<br/>
              🏷️ 할인가 : {product.DiscountPrice}<br/>
              🔻 할인율 : {product.DiscountRate}%
            </StyledCell>
            <StyledCell sx={{textAlign: "center", padding: 0}}>{product.Rank}</StyledCell>
            <StyledCell sx={{textAlign: "center", padding: 0}}>
              <CustomRankChangeCell rankChange={product.RankChange} />
            </StyledCell>
            
            <StyledCell sx={{textAlign: "center" , padding: 0}}>{product.Avg7dRank}</StyledCell>
          </StyledRow>
        ))
      )}
      </TableBody>
    </StyledTable>
  );
}
